/**
* @module contexts/language-state-provider
* @author Charles Blais <charles.blais@canada.ca>
*/

import React, { createContext, useContext, useReducer } from "react";

/**
 * Language state States
 * ---------------------------
 */
export type LanguageState = {
  abbr: string,
  view: string,
  play: string,
  pause: string,
  loading: {
    title: string,
    stationsinfo: string,
  },
  error: {
    title: string,
    stationsinfo: string,
    waveform: string,
  }
  plot: {
    title1m: string,
    title10m: string,
    title1h: string,
    label: string,
  },
  map: {
    available: string,
    unavailable: string,
  },
  instruction: string,
}

export const initialStateEnglish: LanguageState = {
  abbr: 'en',
  view: 'View',
  play: 'Play',
  pause: 'Pause',
  loading: {
    title: 'Loading...',
    stationsinfo: 'Loading stations information for the following network: ',
  },
  error: {
    title: 'Error!',
    stationsinfo: 'Error while loading stations information for the following network: ',
    waveform: 'Channel unavailable',
  },
  plot: {
    title1m: '1 minute of data',
    title10m: '10 minutes of data',
    title1h: '1 hour of data',
    label: 'Local time'
  },
  map: {
    available: 'Available',
    unavailable: 'Unavailable',
  },
  instruction: 'Select any station on the map or in the table below. This will redirect you to the live seismogram of that station. Stations in red or with no link are currently out of operation. The time in the plots represents your local time as being indicated by your system.',
}

export const initialStateFrench: LanguageState = {
  abbr: 'fr',
  view: 'Voir',
  play: 'Jouer',
  pause: 'Pause',
  loading: {
    title: 'Chargement en cours...',
    stationsinfo: 'Chargement de l\'informations sur les stations pour le réseau suivant: ',
  },
  error: {
    title: 'Erreur!',
    stationsinfo: 'Erreur lors du chargement des informations sur les stations pour le réseau suivant: ',
    waveform: 'Canal indisponible',
  },
  plot: {
    title1m: '1 minute de données',
    title10m: '10 minutes de données',
    title1h: '1 heure de données',
    label: 'Heure locale'
  },
  map: {
    available: 'Disponible',
    unavailable: 'Non disponible',
  },
  instruction: 'Sélectionnez n\'importe quelle station sur la carte ou dans le tableau ci-dessous. Cela vous redirigera vers le sismogramme de cette station. Les stations en rouge ou sans lien sont actuellement hors service. L\'heure dans les graphiques représente votre heure locale telle qu\'elle est indiquée par votre système.'
}

/**
 * Reducer settings
 * ----------------
 */
type ActionSetLanguage = {
  type: 'SET_LANGUAGE',
  payload: 'en' | 'fr',
}

type LanguageStateAction = ActionSetLanguage;

const reducer = (
  state: LanguageState,
  action: LanguageStateAction,
): LanguageState => {
  switch (action.type) {
    case 'SET_LANGUAGE':
      switch (action.payload) {
        case 'fr':
          return initialStateFrench;
        default:
          return initialStateEnglish;
      }
    default:
      return state;
  }
}

/**
 * React useContext hook
 * ---------------------
 */
type LanguageContextProps = [
  LanguageState,
  React.Dispatch<LanguageStateAction>,
]

export const LanguageContext = createContext<LanguageContextProps>([initialStateEnglish, () => {}]);

type LanguageStateProviderProps = {
  initialState: LanguageState,
  children: React.ReactNode,
}

export const LanguageStateProvider = ({
  initialState,
  children,
}: LanguageStateProviderProps) => (
  <LanguageContext.Provider value={useReducer(reducer, initialState)}>
    {children}
  </LanguageContext.Provider>
);

export const useLanguageState = () => useContext(LanguageContext);
